import React, { useState, useEffect, useRef } from 'react';
import { API_BASE, getWebhookEndpoint, getWebhookEndpointTemplate } from '../config';
import { Action, WorkflowVariable } from '../types/workflow';

interface PropertiesPanelProps {
    selectedNode: any;
    onUpdate: (nodeId: string, action: Action) => void;
    onClose: () => void;
    onStartInteractive?: (url: string, proxy?: { host: string; port: string; user: string; password: string }) => void;
    onPickElement?: (nodeId: string, parentLocator?: { method: string; value: string } | null) => void;
    onPlayAction?: (action: Action) => Promise<void>;
    interactiveSessionId?: string | null;
    variables?: WorkflowVariable[];
    runtimeVariables?: Record<string, any>;
    onRuntimeVariablesUpdate?: (vars: Record<string, any>) => void;
    onAppendLog?: (level: 'log' | 'error' | 'warn', message: string, nodeLabel?: string) => void;
    workflowId?: string;
}

function deriveLocatorFromHtml(html: string): { method: string; value: string } | null {
    try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const el = doc.body.firstElementChild as HTMLElement;
        if (!el) return null;
        if (el.id) return { method: 'CSS_SELECTOR', value: `#${CSS.escape(el.id)}` };
        if (el.className && typeof el.className === 'string') {
            const classes = el.className.trim().split(/\s+/).filter(Boolean);
            if (classes.length > 0) return { method: 'CSS_SELECTOR', value: el.tagName.toLowerCase() + '.' + classes.map(c => CSS.escape(c)).join('.') };
        }
        return { method: 'CSS_SELECTOR', value: el.tagName.toLowerCase() };
    } catch { return null; }
}

const VariableTextInput: React.FC<{
    value: string; onChange: (val: string) => void; placeholder?: string;
    variables: WorkflowVariable[]; runtimeVariables?: Record<string, any>;
}> = ({ value, onChange, placeholder, variables, runtimeVariables = {} }) => {
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [filter, setFilter] = useState('');
    const inputRef = useRef<HTMLInputElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const designNames = new Set(variables.map(v => v.name));
    const runtimeOnlyVars: WorkflowVariable[] = Object.entries(runtimeVariables)
        .filter(([k, v]) => !designNames.has(k) && !k.startsWith('__') && !Array.isArray(v))
        .map(([name, val]) => ({ name, value: String(val) }));
    const allVariables = [...variables, ...runtimeOnlyVars];
    const filtered = allVariables.filter(v => v.name.toLowerCase().includes(filter.toLowerCase()));
    const handleKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
        const input = inputRef.current; if (!input) return;
        const cursor = input.selectionStart ?? 0; const text = input.value;
        const lastBrace = text.lastIndexOf('{', cursor - 1);
        if (lastBrace !== -1 && cursor > lastBrace) {
            const between = text.slice(lastBrace + 1, cursor);
            if (!between.includes('}')) { setFilter(between.replace(/^\{/, '')); setShowSuggestions(true); return; }
        }
        setShowSuggestions(false);
    };
    const insertVariable = (varName: string) => {
        if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
        const input = inputRef.current; if (!input) return;
        const cursor = input.selectionStart ?? 0; const text = value;
        const lastBrace = text.lastIndexOf('{', cursor - 1);
        const before = lastBrace !== -1 ? text.slice(0, lastBrace) : text.slice(0, cursor);
        const after = text.slice(cursor);
        onChange(`${before}{{${varName}}}${after}`);
        setShowSuggestions(false);
        setTimeout(() => { input.focus(); const pos = before.length + varName.length + 4; input.setSelectionRange(pos, pos); }, 0);
    };
    const handleBlur = () => { closeTimeoutRef.current = setTimeout(() => setShowSuggestions(false), 200); };
    const handleDropdownMouseDown = () => { if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current); };
    useEffect(() => () => { if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current); }, []);
    return (
        <div style={{ position: 'relative' }}>
            <input ref={inputRef} type="text" value={value} onChange={e => onChange(e.target.value)} onKeyUp={handleKeyUp} onClick={handleKeyUp as any} onBlur={handleBlur} placeholder={placeholder} style={{ width: '100%', boxSizing: 'border-box' }} spellCheck={false} />
            {showSuggestions && filtered.length > 0 && (
                <div ref={dropdownRef} onMouseDown={handleDropdownMouseDown} style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid #007acc', borderRadius: '0 0 6px 6px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 9999, maxHeight: 180, overflowY: 'auto' }}>
                    <div style={{ padding: '4px 10px', fontSize: 10, color: '#888', background: '#f5f5f5', borderBottom: '1px solid #eee' }}>📦 Variables — click to insert</div>
                    {filtered.map(v => {
                        const isRuntime = !designNames.has(v.name);
                        const rtVal = runtimeVariables[v.name];
                        const currentVal = rtVal !== undefined ? (Array.isArray(rtVal) ? `[${rtVal.length} items]` : String(rtVal)) : v.value;
                        const hasRuntimeOverride = !isRuntime && rtVal !== undefined && rtVal !== v.value.replace(/^["']|["']$/g, '');
                        return (
                            <div key={v.name} onMouseDown={e => { e.preventDefault(); insertVariable(v.name); }} style={{ padding: '6px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, borderBottom: '1px solid #f0f0f0' }} onMouseEnter={e => (e.currentTarget.style.background = '#e3f2fd')} onMouseLeave={e => (e.currentTarget.style.background = 'white')}>
                                <code style={{ background: isRuntime ? '#fff3e0' : '#e8f4ff', padding: '1px 6px', borderRadius: 3, fontSize: 12, color: isRuntime ? '#e65100' : '#007acc' }}>{`{{${v.name}}}`}</code>
                                {isRuntime && <span style={{ fontSize: 10, color: '#e65100', fontWeight: 'bold' }}>⚡ runtime</span>}
                                {hasRuntimeOverride && <span style={{ fontSize: 10, color: '#e65100', fontWeight: 'bold' }}>⚡ changed</span>}
                                {currentVal && <span style={{ color: '#999', fontSize: 11, fontFamily: 'monospace' }}>= {String(currentVal).substring(0, 30)}</span>}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

const VariableTextArea: React.FC<{
    value: string; onChange: (val: string) => void; placeholder?: string; rows?: number;
    variables: WorkflowVariable[]; runtimeVariables?: Record<string, any>;
}> = ({ value, onChange, placeholder, rows = 4, variables, runtimeVariables = {} }) => {
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [filter, setFilter] = useState('');
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const designNames = new Set(variables.map(v => v.name));
    const runtimeOnlyVars: WorkflowVariable[] = Object.entries(runtimeVariables)
        .filter(([k, v]) => !designNames.has(k) && !k.startsWith('__') && !Array.isArray(v))
        .map(([name, val]) => ({ name, value: String(val) }));
    const allVariables = [...variables, ...runtimeOnlyVars];
    const filtered = allVariables.filter(v => v.name.toLowerCase().includes(filter.toLowerCase()));
    const handleKeyUp = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        const ta = textareaRef.current; if (!ta) return;
        const cursor = ta.selectionStart ?? 0; const text = ta.value;
        const lastBrace = text.lastIndexOf('{', cursor - 1);
        if (lastBrace !== -1 && cursor > lastBrace) {
            const between = text.slice(lastBrace + 1, cursor);
            if (!between.includes('}')) { setFilter(between.replace(/^\{/, '')); setShowSuggestions(true); return; }
        }
        setShowSuggestions(false);
    };
    const insertVariable = (varName: string) => {
        if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
        const ta = textareaRef.current; if (!ta) return;
        const cursor = ta.selectionStart ?? 0; const text = value;
        const lastBrace = text.lastIndexOf('{', cursor - 1);
        const before = lastBrace !== -1 ? text.slice(0, lastBrace) : text.slice(0, cursor);
        const after = text.slice(cursor);
        onChange(`${before}{{${varName}}}${after}`);
        setShowSuggestions(false);
        setTimeout(() => { ta.focus(); const pos = before.length + varName.length + 4; ta.setSelectionRange(pos, pos); }, 0);
    };
    const handleBlur = () => { closeTimeoutRef.current = setTimeout(() => setShowSuggestions(false), 200); };
    const handleDropdownMouseDown = () => { if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current); };
    useEffect(() => () => { if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current); }, []);
    return (
        <div style={{ position: 'relative' }}>
            <textarea ref={textareaRef} value={value} onChange={e => onChange(e.target.value)} onKeyUp={handleKeyUp} onClick={handleKeyUp as any} onBlur={handleBlur} placeholder={placeholder} rows={rows} style={{ width: '100%', boxSizing: 'border-box' }} spellCheck={false} />
            {showSuggestions && filtered.length > 0 && (
                <div ref={dropdownRef} onMouseDown={handleDropdownMouseDown} style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid #007acc', borderRadius: '0 0 6px 6px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 9999, maxHeight: 180, overflowY: 'auto' }}>
                    <div style={{ padding: '4px 10px', fontSize: 10, color: '#888', background: '#f5f5f5', borderBottom: '1px solid #eee' }}>📦 Variables — click to insert</div>
                    {filtered.map(v => {
                        const isRuntime = !designNames.has(v.name);
                        const rtVal = runtimeVariables[v.name];
                        const currentVal = rtVal !== undefined ? (Array.isArray(rtVal) ? `[${rtVal.length} items]` : String(rtVal)) : v.value;
                        const hasRuntimeOverride = !isRuntime && rtVal !== undefined && rtVal !== v.value.replace(/^["']|["']$/g, '');
                        return (
                            <div key={v.name} onMouseDown={e => { e.preventDefault(); insertVariable(v.name); }} style={{ padding: '6px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, borderBottom: '1px solid #f0f0f0' }} onMouseEnter={e => (e.currentTarget.style.background = '#e3f2fd')} onMouseLeave={e => (e.currentTarget.style.background = 'white')}>
                                <code style={{ background: isRuntime ? '#fff3e0' : '#e8f4ff', padding: '1px 6px', borderRadius: 3, fontSize: 12, color: isRuntime ? '#e65100' : '#007acc' }}>{`{{${v.name}}}`}</code>
                                {isRuntime && <span style={{ fontSize: 10, color: '#e65100', fontWeight: 'bold' }}>⚡ runtime</span>}
                                {hasRuntimeOverride && <span style={{ fontSize: 10, color: '#e65100', fontWeight: 'bold' }}>⚡ changed</span>}
                                {currentVal && <span style={{ color: '#999', fontSize: 11, fontFamily: 'monospace' }}>= {String(currentVal).substring(0, 30)}</span>}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

const HtmlVariableInput: React.FC<{
    value: string; onChange: (val: string) => void;
    variables: WorkflowVariable[]; runtimeVariables?: Record<string, any>;
}> = ({ value, onChange, variables, runtimeVariables = {} }) => {
    const [showDropdown, setShowDropdown] = useState(false);
    const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const isHtml = (v: any) => typeof v === 'string' && v.trim().startsWith('<') && v.trim().endsWith('>');
    const isHtmlArray = (v: any) => Array.isArray(v) && v.length > 0 && v.every((i: any) => isHtml(i));
    const designNames = new Set(variables.map(v => v.name));
    const htmlVars = [
        ...variables.filter(v => isHtml(v.value)).map(v => ({ name: v.name, isRuntime: false })),
        ...Object.entries(runtimeVariables).filter(([k, v]) => !designNames.has(k) && !k.startsWith('__') && (isHtml(v) || isHtmlArray(v))).map(([k, v]) => ({ name: k, isRuntime: true, isArray: isHtmlArray(v) })),
        ...variables.filter(v => !isHtml(v.value) && runtimeVariables[v.name] !== undefined && (isHtml(runtimeVariables[v.name]) || isHtmlArray(runtimeVariables[v.name]))).map(v => ({ name: v.name, isRuntime: false, isArray: isHtmlArray(runtimeVariables[v.name]) })),
    ];
    const handleBlur = () => { closeTimeoutRef.current = setTimeout(() => setShowDropdown(false), 200); };
    const handleDropdownMouseDown = () => { if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current); };
    useEffect(() => () => { if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current); }, []);
    return (
        <div style={{ position: 'relative' }}>
            <input type="text" value={value} onChange={e => onChange(e.target.value)} onFocus={() => setShowDropdown(true)} onBlur={handleBlur} placeholder="Select html variable or type name" style={{ width: '100%', boxSizing: 'border-box' }} spellCheck={false} />
            {showDropdown && (
                <div onMouseDown={handleDropdownMouseDown} style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid #E91E63', borderRadius: '0 0 6px 6px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 9999, maxHeight: 200, overflowY: 'auto' }}>
                    {htmlVars.length === 0 ? <div style={{ padding: '10px 12px', fontSize: 12, color: '#aaa' }}>No html variables yet — run a <strong>Get Element</strong> node first</div> : (
                        <>
                            <div style={{ padding: '4px 10px', fontSize: 10, color: '#888', background: '#fce4ec', borderBottom: '1px solid #f8bbd0' }}>🏷️ HTML variables — select parent element</div>
                            {htmlVars.map(v => (
                                <div key={v.name} onMouseDown={e => { e.preventDefault(); onChange(v.name); setShowDropdown(false); }} style={{ padding: '6px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, borderBottom: '1px solid #f0f0f0', background: value === v.name ? '#fce4ec' : 'white' }} onMouseEnter={e => (e.currentTarget.style.background = '#fce4ec')} onMouseLeave={e => (e.currentTarget.style.background = value === v.name ? '#fce4ec' : 'white')}>
                                    <code style={{ background: '#fce4ec', padding: '1px 6px', borderRadius: 3, fontSize: 12, color: '#E91E63' }}>{v.name}</code>
                                    <span style={{ fontSize: 10, color: '#E91E63', fontWeight: 'bold' }}>{(v as any).isArray ? 'html[]' : 'html'}</span>
                                    {v.isRuntime && <span style={{ fontSize: 10, color: '#e65100', fontWeight: 'bold' }}>⚡ runtime</span>}
                                </div>
                            ))}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

const VariableNameInput: React.FC<{
    value: string; onChange: (val: string) => void; placeholder?: string;
    variables: WorkflowVariable[]; runtimeVariables?: Record<string, any>;
}> = ({ value, onChange, placeholder, variables, runtimeVariables = {} }) => {
    const [showDropdown, setShowDropdown] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const designNames = new Set(variables.map(v => v.name));
    const runtimeOnlyEntries = Object.entries(runtimeVariables).filter(([k]) => !designNames.has(k) && !k.startsWith('__'));
    const allNames = [...variables.map(v => ({ name: v.name, isRuntime: false })), ...runtimeOnlyEntries.map(([k]) => ({ name: k, isRuntime: true }))];
    const filtered = value.trim() ? allNames.filter(v => v.name.toLowerCase().includes(value.toLowerCase())) : allNames;
    const isNew = value.trim() !== '' && !allNames.some(v => v.name === value.trim());
    const handleBlur = () => { closeTimeoutRef.current = setTimeout(() => setShowDropdown(false), 200); };
    const handleDropdownMouseDown = () => { if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current); };
    useEffect(() => () => { if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current); }, []);
    return (
        <div style={{ position: 'relative' }}>
            <input ref={inputRef} type="text" value={value} onChange={e => onChange(e.target.value)} onFocus={() => setShowDropdown(true)} onBlur={handleBlur} placeholder={placeholder || 'Select or type variable name'} style={{ width: '100%', boxSizing: 'border-box', borderColor: isNew ? '#e65100' : undefined }} spellCheck={false} />
            {isNew && <div style={{ fontSize: 10, color: '#e65100', marginTop: 3 }}>⚡ New runtime variable will be created</div>}
            {showDropdown && (
                <div ref={dropdownRef} onMouseDown={handleDropdownMouseDown} style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid #007acc', borderRadius: '0 0 6px 6px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 9999, maxHeight: 200, overflowY: 'auto' }}>
                    {allNames.length === 0 ? <div style={{ padding: '8px 12px', fontSize: 12, color: '#aaa' }}>No variables defined yet</div> : (
                        <>
                            <div style={{ padding: '4px 10px', fontSize: 10, color: '#888', background: '#f5f5f5', borderBottom: '1px solid #eee' }}>📦 Select variable to store result into</div>
                            {filtered.map(v => (
                                <div key={v.name} onMouseDown={e => { e.preventDefault(); onChange(v.name); setShowDropdown(false); }} style={{ padding: '6px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, borderBottom: '1px solid #f0f0f0', background: value === v.name ? '#e3f2fd' : 'white' }} onMouseEnter={e => (e.currentTarget.style.background = '#e3f2fd')} onMouseLeave={e => (e.currentTarget.style.background = value === v.name ? '#e3f2fd' : 'white')}>
                                    <code style={{ background: v.isRuntime ? '#fff3e0' : '#e8f4ff', padding: '1px 6px', borderRadius: 3, fontSize: 12, color: v.isRuntime ? '#e65100' : '#007acc' }}>{v.name}</code>
                                    {v.isRuntime && <span style={{ fontSize: 10, color: '#e65100', fontWeight: 'bold' }}>⚡ runtime</span>}
                                </div>
                            ))}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

const StringVariableInput: React.FC<{
    value: string; onChange: (val: string) => void;
    variables: WorkflowVariable[]; runtimeVariables?: Record<string, any>;
}> = ({ value, onChange, variables, runtimeVariables = {} }) => {
    const [showDropdown, setShowDropdown] = useState(false);
    const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const isPureString = (val: any): boolean => {
        if (typeof val !== 'string') return false;
        if (val === '') return true;
        // Exclude JSON arrays/objects and numeric strings
        try { const p = JSON.parse(val); if (typeof p !== 'string') return false; } catch {}
        return true;
    };
    const designNames = new Set(variables.map(v => v.name));
    const stringVars = [
        ...variables.filter(v => {
            const rt = runtimeVariables[v.name];
            if (rt !== undefined) return isPureString(rt);
            return isPureString(v.value);
        }).map(v => ({ name: v.name, isRuntime: false })),
        ...Object.entries(runtimeVariables)
            .filter(([k, v]) => !designNames.has(k) && !k.startsWith('__') && isPureString(v))
            .map(([k]) => ({ name: k, isRuntime: true })),
    ];
    const handleBlur = () => { closeTimeoutRef.current = setTimeout(() => setShowDropdown(false), 200); };
    const handleDropdownMouseDown = () => { if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current); };
    useEffect(() => () => { if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current); }, []);
    const filtered = value.trim() ? stringVars.filter(v => v.name.toLowerCase().includes(value.toLowerCase())) : stringVars;
    return (
        <div style={{ position: 'relative' }}>
            <input type="text" value={value} onChange={e => onChange(e.target.value)} onFocus={() => setShowDropdown(true)} onBlur={handleBlur} placeholder="Select string variable or type name" style={{ width: '100%', boxSizing: 'border-box' }} spellCheck={false} />
            {showDropdown && (
                <div onMouseDown={handleDropdownMouseDown} style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid #007acc', borderRadius: '0 0 6px 6px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 9999, maxHeight: 200, overflowY: 'auto' }}>
                    {filtered.length === 0 ? (
                        <div style={{ padding: '10px 12px', fontSize: 12, color: '#aaa' }}>No string variables yet — use a code block or API call to create one</div>
                    ) : (
                        <>
                            <div style={{ padding: '4px 10px', fontSize: 10, color: '#888', background: '#f5f5f5', borderBottom: '1px solid #eee' }}>📄 String variables — select one</div>
                            {filtered.map(v => (
                                <div key={v.name} onMouseDown={e => { e.preventDefault(); onChange(v.name); setShowDropdown(false); }}
                                    style={{ padding: '6px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, borderBottom: '1px solid #f0f0f0', background: value === v.name ? '#e3f2fd' : 'white' }}
                                    onMouseEnter={e => (e.currentTarget.style.background = '#e3f2fd')}
                                    onMouseLeave={e => (e.currentTarget.style.background = value === v.name ? '#e3f2fd' : 'white')}>
                                    <code style={{ background: v.isRuntime ? '#fff3e0' : '#e8f4ff', padding: '1px 6px', borderRadius: 3, fontSize: 12, color: v.isRuntime ? '#e65100' : '#007acc' }}>{v.name}</code>
                                    {v.isRuntime && <span style={{ fontSize: 10, color: '#e65100', fontWeight: 'bold' }}>⚡ runtime</span>}
                                </div>
                            ))}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

const ArrayVariableInput: React.FC<{
    value: string; onChange: (val: string) => void;
    variables: WorkflowVariable[]; runtimeVariables?: Record<string, any>;
}> = ({ value, onChange, variables, runtimeVariables = {} }) => {
    const [showDropdown, setShowDropdown] = useState(false);
    const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const isArray = (v: any) => Array.isArray(v);
    const parseJsonArray = (s: string): any[] | null => { try { const p = JSON.parse(s); return Array.isArray(p) ? p : null; } catch { return null; } };
    const designNames = new Set(variables.map(v => v.name));
    const arrayVars = [
        ...variables.filter(v => {
            const rt = runtimeVariables[v.name];
            if (isArray(rt)) return true;
            if (typeof rt === 'string' && parseJsonArray(rt)) return true;
            if (rt === undefined && typeof v.value === 'string' && parseJsonArray(v.value)) return true;
            return false;
        }).map(v => {
            const rt = runtimeVariables[v.name];
            if (isArray(rt)) return { name: v.name, isRuntime: false, count: (rt as any[]).length };
            const parsed = parseJsonArray(typeof rt === 'string' ? rt : v.value)!;
            return { name: v.name, isRuntime: false, count: parsed.length };
        }),
        ...Object.entries(runtimeVariables)
            .filter(([k, v]) => !designNames.has(k) && !k.startsWith('__') && (isArray(v) || (typeof v === 'string' && parseJsonArray(v) !== null)))
            .map(([k, v]) => {
                if (isArray(v)) return { name: k, isRuntime: true, count: (v as any[]).length };
                return { name: k, isRuntime: true, count: parseJsonArray(v as string)!.length };
            }),
    ];
    const handleBlur = () => { closeTimeoutRef.current = setTimeout(() => setShowDropdown(false), 200); };
    const handleDropdownMouseDown = () => { if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current); };
    useEffect(() => () => { if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current); }, []);
    const filtered = value.trim() ? arrayVars.filter(v => v.name.toLowerCase().includes(value.toLowerCase())) : arrayVars;
    return (
        <div style={{ position: 'relative' }}>
            <input
                type="text"
                value={value}
                onChange={e => onChange(e.target.value)}
                onFocus={() => setShowDropdown(true)}
                onBlur={handleBlur}
                placeholder="Select array variable or type name"
                style={{ width: '100%', boxSizing: 'border-box' }}
                spellCheck={false}
            />
            {showDropdown && (
                <div onMouseDown={handleDropdownMouseDown} style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid #007acc', borderRadius: '0 0 6px 6px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 9999, maxHeight: 200, overflowY: 'auto' }}>
                    {filtered.length === 0 ? (
                        <div style={{ padding: '10px 12px', fontSize: 12, color: '#aaa' }}>No array variables yet — use <strong>Get Elements</strong>, a code block, or define a design-time array variable</div>
                    ) : (
                        <>
                            <div style={{ padding: '4px 10px', fontSize: 10, color: '#888', background: '#f5f5f5', borderBottom: '1px solid #eee' }}>➰ Array variables — select to iterate</div>
                            {filtered.map(v => (
                                <div key={v.name} onMouseDown={e => { e.preventDefault(); onChange(v.name); setShowDropdown(false); }}
                                    style={{ padding: '6px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, borderBottom: '1px solid #f0f0f0', background: value === v.name ? '#e3f2fd' : 'white' }}
                                    onMouseEnter={e => (e.currentTarget.style.background = '#e3f2fd')}
                                    onMouseLeave={e => (e.currentTarget.style.background = value === v.name ? '#e3f2fd' : 'white')}>
                                    <code style={{ background: v.isRuntime ? '#fff3e0' : '#e8f4ff', padding: '1px 6px', borderRadius: 3, fontSize: 12, color: v.isRuntime ? '#e65100' : '#007acc' }}>{v.name}</code>
                                    <span style={{ fontSize: 10, color: '#888', fontWeight: 'bold' }}>[{v.count} items]</span>
                                    {v.isRuntime && <span style={{ fontSize: 10, color: '#e65100', fontWeight: 'bold' }}>⚡ runtime</span>}
                                </div>
                            ))}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

const PropertiesPanel: React.FC<PropertiesPanelProps> = ({
    selectedNode, onUpdate, onClose, onStartInteractive, onPickElement,
    onPlayAction, interactiveSessionId, variables = [], runtimeVariables = {},
    onRuntimeVariablesUpdate, onAppendLog, workflowId
}) => {
    const [action, setAction] = useState<Action>(selectedNode?.data?.action || {});
    const actionInitRef = useRef(false);
    const [startingInteractive, setStartingInteractive] = useState(false);
    const [playingAction, setPlayingAction] = useState(false);
    const [playResult, setPlayResult] = useState<{ success: boolean; message: string } | null>(null);
    const [pos, setPos] = useState({ x: window.innerWidth - 390, y: 20 });
    const [zIndex, setZIndex] = useState(1000);
    const posRef = useRef(pos);
    const codeTextareaRef = useRef<HTMLTextAreaElement>(null);
    useEffect(() => { posRef.current = pos; }, [pos]);

    const prevNodeIdRef = useRef<string | null>(null);
    useEffect(() => {
        if (prevNodeIdRef.current !== selectedNode?.id) {
            prevNodeIdRef.current = selectedNode?.id ?? null;
            setPlayResult(null);
        }
    }, [selectedNode?.id]);

    useEffect(() => {
        if (selectedNode?.data?.action) {
            setAction(selectedNode.data.action);
            actionInitRef.current = false;
        }
        const status = selectedNode?.data?.playStatus;
        const message = selectedNode?.data?.playMessage;
        if (status === 'success' && message) setPlayResult({ success: true, message: `✅ ${message}` });
        else if (status === 'error' && message) setPlayResult({ success: false, message: `❌ ${message}` });
        else if (status === 'idle') setPlayResult(null);
    }, [selectedNode, selectedNode?.data?.action, selectedNode?.data?.playStatus, selectedNode?.data?.playMessage]);

    // Auto-save action changes so switching nodes preserves edits
    useEffect(() => {
        if (!actionInitRef.current) { actionInitRef.current = true; return; }
        if (selectedNode?.id) onUpdate(selectedNode.id, action);
    }, [action]);

    const onHeaderMouseDown = (e: React.MouseEvent) => {
        if ((e.target as HTMLElement).tagName === 'BUTTON') return;
        e.preventDefault();
        bringToFront();
        const startX = e.clientX; const startY = e.clientY;
        const origX = posRef.current.x; const origY = posRef.current.y;
        const onMove = (ev: MouseEvent) => { setPos({ x: Math.max(0, Math.min(window.innerWidth - 370, origX + ev.clientX - startX)), y: Math.max(0, Math.min(window.innerHeight - 60, origY + ev.clientY - startY)) }); };
        const onUp = () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
    };
    const bringToFront = () => setZIndex(z => Math.max(z, 1001) + 1);

    if (!selectedNode) return null;

    const handleSave = () => { onUpdate(selectedNode.id, action); onClose(); };
    const handleSaveWithoutClose = () => { onUpdate(selectedNode.id, action); };

    const handlePlayAction = async () => {
        setPlayResult(null);
        setPlayingAction(true);
        handleSaveWithoutClose();
        try {
            const designVars: Record<string, string> = {};
            for (const v of variables) designVars[v.name] = v.value.replace(/^["']|["']$/g, '');
            const mergedVars = { ...designVars, ...runtimeVariables };

            // Webhook test: register listener then long-poll
            if (action.type === 'webhook') {
                if (!workflowId) throw new Error('Save the workflow first to get a workflow ID');
                await fetch(`${API_BASE}/webhooks/${workflowId}/test`, { method: 'POST' });
                const resultRes = await fetch(`${API_BASE}/webhooks/${workflowId}/test/wait`);
                const result = await resultRes.json();
                if (result.body) {
                    if (action.store_as) {
                        onRuntimeVariablesUpdate?.({ ...runtimeVariables, [action.store_as]: result.body });
                    }
                    setPlayResult({ success: true, message: `✅ Received! Body: ${JSON.stringify(result.body).substring(0, 150)}` });
                } else {
                    setPlayResult({ success: false, message: result.error || 'No request received' });
                }
                return;
            }

            // Storage: last_file
            if (action.type === 'last_file') {
                if (!workflowId) throw new Error('Save the workflow first');
                const res = await fetch(`${API_BASE}/workflows/${workflowId}/storage/last-file`, { method: 'POST' });
                const result = await res.json();
                if (result.success && action.store_as) {
                    onRuntimeVariablesUpdate?.({ ...runtimeVariables, [action.store_as]: result.filename });
                    setPlayResult({ success: true, message: `✅ Last file: ${result.filename}` });
                } else {
                    setPlayResult({ success: false, message: `❌ ${result.message || 'No files found'}` });
                }
                return;
            }

            // Storage: rename_file
            if (action.type === 'rename_file') {
                if (!workflowId) throw new Error('Save the workflow first');
                const designVarsLocal: Record<string, string> = {};
                for (const v of variables) designVarsLocal[v.name] = v.value.replace(/^["']|["']$/g, '');
                const allVars = { ...designVarsLocal, ...runtimeVariables };
                const resolveVars = (s: string) => s.replace(/\{\{(\w+)\}\}/g, (_, name) => allVars[name] !== undefined ? String(allVars[name]) : `{{${name}}}`);
                const res = await fetch(`${API_BASE}/workflows/${workflowId}/storage/rename`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ oldName: resolveVars(action.old_name || ''), newName: resolveVars(action.new_name || '') }),
                });
                const result = await res.json();
                if (result.success) {
                    setPlayResult({ success: true, message: `✅ ${result.message}` });
                } else {
                    setPlayResult({ success: false, message: `❌ ${result.message}` });
                }
                return;
            }

            // Storage: create_file
            if (action.type === 'create_file') {
                if (!workflowId) throw new Error('Save the workflow first');
                const designVarsLocal: Record<string, string> = {};
                for (const v of variables) designVarsLocal[v.name] = v.value.replace(/^["']|["']$/g, '');
                const allVars = { ...designVarsLocal, ...runtimeVariables };
                const resolveVars = (s: string) => s.replace(/\{\{(\w+)\}\}/g, (_, name) => allVars[name] !== undefined ? String(allVars[name]) : `{{${name}}}`);
                const dataVar = action.data || '';
                const dataValue = allVars[dataVar];
                if (dataValue === undefined) throw new Error(`Variable "${dataVar}" is not set`);
                const res = await fetch(`${API_BASE}/workflows/${workflowId}/storage/create-file`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ file_type: action.file_type || 'pdf', data: dataValue, file_name: resolveVars(action.file_name || '') }),
                });
                const result = await res.json();
                if (result.success) {
                    setPlayResult({ success: true, message: `✅ ${result.message}` });
                } else {
                    setPlayResult({ success: false, message: `❌ ${result.message}` });
                }
                return;
            }

            // Storage: read_file
            if (action.type === 'read_file') {
                if (!workflowId) throw new Error('Save the workflow first');
                const designVarsLocal: Record<string, string> = {};
                for (const v of variables) designVarsLocal[v.name] = v.value.replace(/^["']|["']$/g, '');
                const allVars = { ...designVarsLocal, ...runtimeVariables };
                const resolveVars = (s: string) => s.replace(/\{\{(\w+)\}\}/g, (_, name) => allVars[name] !== undefined ? String(allVars[name]) : `{{${name}}}`);
                const res = await fetch(`${API_BASE}/workflows/${workflowId}/storage/read-file`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ file_type: action.file_type || 'pdf', file_name: resolveVars(action.file_name || '') }),
                });
                const result = await res.json();
                if (result.success) {
                    if (action.store_as) {
                        const newRuntime = { ...runtimeVariables };
                        newRuntime[action.store_as] = typeof result.data === 'string' ? result.data : JSON.stringify(result.data);
                        onRuntimeVariablesUpdate?.(newRuntime);
                    }
                    setPlayResult({ success: true, message: `✅ ${result.message}` });
                } else {
                    setPlayResult({ success: false, message: `❌ ${result.message}` });
                }
                return;
            }

            // AI Agent: send prompt to backend
            if (action.type === 'ai_agent') {
                const designVarsLocal: Record<string, string> = {};
                for (const v of variables) designVarsLocal[v.name] = v.value.replace(/^["']|["']$/g, '');
                const allVars = { ...designVarsLocal, ...runtimeVariables };
                const res = await fetch(`${API_BASE}/interactive/ai-agent`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        system_prompt: action.system_prompt || '',
                        user_prompt: action.user_prompt || '',
                        variables: allVars,
                        output_type: action.output_type || 'string',
                    }),
                });
                const result = await res.json();
                if (result.success) {
                    if (action.store_as) {
                        const newRuntime = { ...runtimeVariables };
                        newRuntime[action.store_as] = result.response;
                        onRuntimeVariablesUpdate?.(newRuntime);
                    }
                    setPlayResult({ success: true, message: `✅ ${result.message}` });
                } else {
                    setPlayResult({ success: false, message: `❌ ${result.message}` });
                }
                return;
            }

            // API Call: make request via backend to avoid CORS
            if (action.type === 'api_call') {
                const res = await fetch(`${API_BASE}/interactive/api-call`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        method: action.method || 'GET',
                        url: action.url || '',
                        headers: action.headers || {},
                        requestBody: action.body || {},
                        variables: mergedVars,
                    }),
                });
                const result = await res.json();
                if (result.success) {
                    const newRuntime: Record<string, any> = { ...runtimeVariables };
                    if (action.store_as) {
                        newRuntime[action.store_as] = typeof result.responseBody === 'object' ? JSON.stringify(result.responseBody) : String(result.responseBody);
                    }
                    if (action.store_status_as) {
                        newRuntime[action.store_status_as] = String(result.status);
                    }
                    onRuntimeVariablesUpdate?.(newRuntime);
                    setPlayResult({ success: true, message: `✅ ${result.status} — ${String(result.responseBody).substring(0, 200)}` });
                } else {
                    setPlayResult({ success: false, message: `❌ ${result.message}` });
                }
                return;
            }

            if (action.type === 'code') {
                const res = await fetch(`${API_BASE}/interactive/run-code`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ code: action.code || '', variables: mergedVars }),
                });
                const result = await res.json();
                if (result.success) {
                    if (result.message && result.message !== 'Code executed successfully (no output)') {
                        const label = selectedNode?.data?.label || 'Code';
                        result.message.split('\n').forEach((line: string) => {
                            if (line.trim()) {
                                const level = line.startsWith('[error]') ? 'error' : line.startsWith('[warn]') ? 'warn' : 'log';
                                onAppendLog?.(level as 'log' | 'error' | 'warn', line.replace(/^\[(error|warn)\]\s*/, ''), label);
                            }
                        });
                    }
                    try {
                        const updatedVars: Record<string, any> = {};
                        for (const [k, v] of Object.entries(mergedVars)) {
                            if (typeof v === 'string') {
                                const t = v.trim();
                                if ((t.startsWith('[') && t.endsWith(']')) || (t.startsWith('{') && t.endsWith('}'))) {
                                    try { updatedVars[k] = JSON.parse(t); continue; } catch {}
                                }
                                if (t === 'true') { updatedVars[k] = true; continue; }
                                if (t === 'false') { updatedVars[k] = false; continue; }
                                if (t !== '' && !isNaN(Number(t))) { updatedVars[k] = Number(t); continue; }
                            }
                            updatedVars[k] = v;
                        }
                        const fn = new Function('variables', 'console', `with(variables) { ${action.code || ''} }`);
                        fn(updatedVars, { log: () => {}, error: () => {}, warn: () => {} });
                        const newRuntime: Record<string, any> = { ...runtimeVariables };
                        for (const [k, v] of Object.entries(updatedVars)) {
                            if (k.startsWith('__')) continue;
                            if (typeof v === 'object') newRuntime[k] = JSON.stringify(v);
                            else if (v !== undefined) newRuntime[k] = String(v);
                        }
                        onRuntimeVariablesUpdate?.(newRuntime);
                    } catch (e) {}
                    setPlayResult({ success: true, message: result.message || 'Code executed successfully' });
                } else {
                    setPlayResult({ success: false, message: result.message || 'Code failed' });
                }
                return;
            }

            await onPlayAction?.({ ...action, variables: mergedVars } as any);
            setPlayResult({ success: true, message: '✅ Action executed successfully' });
        } catch (err: any) {
            setPlayResult({ success: false, message: `❌ ${err.message}` });
        } finally {
            setPlayingAction(false);
        }
    };

    const pickPlayButtons = (
        <div style={{ display: 'flex', gap: '5px' }}>
            <button
                onClick={() => {
                    handleSaveWithoutClose();
                    let parentLocator = null;
                    if (action.parent) {
                        const locatorStr = runtimeVariables[`__locator_${action.parent}`];
                        if (locatorStr) { try { parentLocator = JSON.parse(locatorStr); } catch {} }
                        if (!parentLocator) {
                            // Variable was assigned via code (e.g. variables.B = variables.A)
                            // Find any variable with a matching value that has a stored locator
                            const parentHtml = runtimeVariables[action.parent];
                            if (parentHtml != null) {
                                for (const [key, val] of Object.entries(runtimeVariables)) {
                                    if (key.startsWith('__')) continue;
                                    // Direct match: B = A where A is a single element
                                    if (val === parentHtml && runtimeVariables[`__locator_${key}`]) {
                                        try { parentLocator = JSON.parse(runtimeVariables[`__locator_${key}`]); } catch {}
                                        if (parentLocator) break;
                                    }
                                    // Array match: B = A[i] where A is from get_elements
                                    // val may be an actual array or a JSON-stringified array
                                    let arrVal = val;
                                    if (typeof arrVal === 'string') {
                                        try { const parsed = JSON.parse(arrVal); if (Array.isArray(parsed)) arrVal = parsed; } catch {}
                                    }
                                    if (Array.isArray(arrVal) && runtimeVariables[`__locator_${key}`]) {
                                        const idx = arrVal.indexOf(parentHtml);
                                        if (idx !== -1) {
                                            try { parentLocator = { ...JSON.parse(runtimeVariables[`__locator_${key}`]), index: idx }; } catch {}
                                            if (parentLocator) break;
                                        }
                                    }
                                }
                            }
                            if (!parentLocator && typeof parentHtml === 'string' && parentHtml.trim().startsWith('<')) {
                                parentLocator = deriveLocatorFromHtml(parentHtml);
                            }
                        }
                    }
                    onPickElement?.(selectedNode.id, parentLocator);
                }}
                disabled={!interactiveSessionId}
                title={interactiveSessionId ? 'Pick element from interactive browser' : 'Open an interactive browser first'}
                style={{ padding: '4px 10px', background: interactiveSessionId ? '#007acc' : '#ccc', color: 'white', border: 'none', borderRadius: '4px', cursor: interactiveSessionId ? 'pointer' : 'not-allowed', fontSize: '12px', whiteSpace: 'nowrap' }}
            >🎯 Pick</button>
            {action.type !== 'wait_for_element' && <button
                onClick={handlePlayAction}
                disabled={!interactiveSessionId || playingAction || !action.locator?.value}
                title={!interactiveSessionId ? 'Open an interactive browser first' : !action.locator?.value ? 'Enter a locator value first' : 'Run this action in the interactive browser'}
                style={{ padding: '4px 10px', background: playingAction ? '#888' : (!interactiveSessionId || !action.locator?.value) ? '#ccc' : '#4CAF50', color: 'white', border: 'none', borderRadius: '4px', cursor: (interactiveSessionId && !playingAction && action.locator?.value) ? 'pointer' : 'not-allowed', fontSize: '12px', whiteSpace: 'nowrap' }}
            >{playingAction ? '⏳' : '▶ Play'}</button>}
        </div>
    );

    return (
        <div onMouseDown={bringToFront} style={{ position: 'fixed', left: pos.x, top: pos.y, width: 360, background: 'white', borderRadius: 8, boxShadow: '0 4px 24px rgba(0,0,0,0.35)', zIndex, maxHeight: 'calc(100vh - 40px)', display: 'flex', flexDirection: 'column' }}>
            <div onMouseDown={onHeaderMouseDown} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 15px', background: '#007acc', color: 'white', borderRadius: '8px 8px 0 0', cursor: 'move', userSelect: 'none', flexShrink: 0 }}>
                <h3 style={{ margin: 0, fontSize: 15 }}>⚙️ {action.type.replace(/_/g, ' ')}</h3>
                <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'white', fontSize: 24, cursor: 'pointer', padding: 0, lineHeight: 1 }}>×</button>
            </div>
            <div className="properties-body" style={{ overflowY: 'auto', flex: 1 }}>

                {action.type === 'open_browser' && (
                    <>
                        <div className="form-group">
                            <label>URL:</label>
                            <VariableTextInput value={action.url || ''} onChange={val => setAction({ ...action, url: val })} placeholder="https://example.com or {{url_variable}}" variables={variables} runtimeVariables={runtimeVariables} />
                            {variables.length > 0 && <p style={{ fontSize: '11px', color: '#888', marginTop: 4 }}>💡 Type <code style={{ background: '#f0f0f0', padding: '0 3px' }}>{`{`}</code> to insert a variable</p>}
                        </div>
                        <div className="form-group">
                            <button className="btn-interactive" onClick={async () => {
                                setStartingInteractive(true);
                                onUpdate(selectedNode.id, action);
                                try {
                                    const designVars: Record<string, string> = {};
                                    for (const v of variables) designVars[v.name] = v.value.replace(/^["']|["']$/g, '');
                                    const mergedVars = { ...designVars, ...runtimeVariables };
                                    const resolvedUrl = (action.url || '').replace(/\{\{(\w+)\}\}/g, (_, key) => mergedVars[key] !== undefined ? String(mergedVars[key]) : '');
                                    const proxy = action.use_proxy ? { host: action.proxy_host || '', port: action.proxy_port || '', user: action.proxy_user || '', password: action.proxy_password || '' } : undefined;
                                    await onStartInteractive?.(resolvedUrl, proxy);
                                } finally { setStartingInteractive(false); }
                            }} disabled={startingInteractive || !action.url || action.url.trim() === '' || action.url === 'https://'}
                                style={{ width: '100%', padding: '8px', background: startingInteractive ? '#888' : interactiveSessionId ? '#FF9800' : '#1B5E20', color: 'white', border: 'none', borderRadius: '4px', cursor: startingInteractive ? 'wait' : 'pointer', fontWeight: 'bold', fontSize: '13px', opacity: startingInteractive ? 0.8 : 1 }}>
                                {startingInteractive ? '⏳ Opening browser...' : interactiveSessionId ? '🔄 Reopen Interactive Browser' : '🌍 Open Interactive Browser'}
                            </button>
                            <p style={{ fontSize: '11px', color: '#666', marginTop: 5 }}>Opens the URL in an interactive browser for element picking.</p>
                        </div>

                        {/* Proxy settings */}
                        <div className="form-group" style={{ marginTop: 8 }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                                <span>🔒 Use Proxy</span>
                                <div
                                    onClick={() => setAction({ ...action, use_proxy: !action.use_proxy })}
                                    style={{
                                        width: 36, height: 20, borderRadius: 10,
                                        background: action.use_proxy ? '#4CAF50' : '#ccc',
                                        position: 'relative', cursor: 'pointer', transition: 'background 0.2s',
                                    }}
                                >
                                    <div style={{
                                        width: 16, height: 16, borderRadius: '50%', background: 'white',
                                        position: 'absolute', top: 2, left: action.use_proxy ? 18 : 2,
                                        transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                                    }} />
                                </div>
                            </label>
                        </div>
                        {action.use_proxy && (
                            <div style={{ background: '#f5f5f5', borderRadius: 6, padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 6, border: '1px solid #e0e0e0' }}>
                                <div style={{ display: 'flex', gap: 6 }}>
                                    <div style={{ flex: 2 }}>
                                        <label style={{ fontSize: 11, color: '#666' }}>Host</label>
                                        <input type="text" value={action.proxy_host || ''} onChange={e => setAction({ ...action, proxy_host: e.target.value })} placeholder="proxy.example.com" style={{ width: '100%', padding: '5px 8px', fontSize: 12, border: '1px solid #ccc', borderRadius: 3, boxSizing: 'border-box' }} />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <label style={{ fontSize: 11, color: '#666' }}>Port</label>
                                        <input type="text" value={action.proxy_port || ''} onChange={e => setAction({ ...action, proxy_port: e.target.value })} placeholder="8080" style={{ width: '100%', padding: '5px 8px', fontSize: 12, border: '1px solid #ccc', borderRadius: 3, boxSizing: 'border-box' }} />
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: 6 }}>
                                    <div style={{ flex: 1 }}>
                                        <label style={{ fontSize: 11, color: '#666' }}>Username</label>
                                        <input type="text" value={action.proxy_user || ''} onChange={e => setAction({ ...action, proxy_user: e.target.value })} placeholder="(optional)" style={{ width: '100%', padding: '5px 8px', fontSize: 12, border: '1px solid #ccc', borderRadius: 3, boxSizing: 'border-box' }} />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <label style={{ fontSize: 11, color: '#666' }}>Password</label>
                                        <input type="password" value={action.proxy_password || ''} onChange={e => setAction({ ...action, proxy_password: e.target.value })} placeholder="(optional)" style={{ width: '100%', padding: '5px 8px', fontSize: 12, border: '1px solid #ccc', borderRadius: 3, boxSizing: 'border-box' }} />
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}

                {action.type === 'close_browser' && (
                    <div className="form-group"><p style={{ fontSize: '12px', color: '#666', margin: 0 }}>This action closes the browser. No configuration needed.</p></div>
                )}

                {(action.type === 'click' || action.type === 'hover' || action.type === 'write' || action.type === 'get_element' || action.type === 'get_elements' || action.type === 'wait_for_element') && (
                    <div className="form-group">
                        <label>Locator Method:</label>
                        <select value={action.locator?.method || 'CSS_SELECTOR'} onChange={e => setAction({ ...action, locator: { ...action.locator!, method: e.target.value as any } })}>
                            <option value="CSS_SELECTOR">CSS Selector</option>
                            <option value="XPATH">XPath</option>
                            <option value="ID">ID</option>
                            <option value="NAME">Name</option>
                            <option value="CLASS_NAME">Class Name</option>
                        </select>
                    </div>
                )}

                {(action.type === 'click' || action.type === 'hover' || action.type === 'write' || action.type === 'get_element' || action.type === 'get_elements' || action.type === 'wait_for_element') && (
                    <div className="form-group">
                        <label>Locator Value:</label>
                        <div style={{ display: 'flex', gap: '5px' }}>
                            <input type="text" value={action.locator?.value || ''} onChange={e => setAction({ ...action, locator: { ...action.locator!, value: e.target.value } })} placeholder="Enter selector" style={{ flex: 1 }} />
                            {pickPlayButtons}
                        </div>
                        {playResult && <p style={{ fontSize: '11px', marginTop: 5, color: playResult.success ? '#4CAF50' : '#f44336' }}>{playResult.message}</p>}
                    </div>
                )}

                {(action.type === 'click' || action.type === 'hover' || action.type === 'write') && (
                    <div className="form-group">
                        <label>Parent Element (optional):</label>
                        <HtmlVariableInput value={action.parent || ''} onChange={val => setAction({ ...action, parent: val })} variables={variables} runtimeVariables={runtimeVariables} />
                    </div>
                )}

                {action.type === 'write' && (
                    <div className="form-group">
                        <label>Text to Write:</label>
                        <VariableTextInput value={action.text || ''} onChange={val => setAction({ ...action, text: val })} placeholder='Enter text or type { to insert a variable' variables={variables} runtimeVariables={runtimeVariables} />
                        {variables.length > 0 && <p style={{ fontSize: '11px', color: '#888', marginTop: 4 }}>💡 Type <code style={{ background: '#f0f0f0', padding: '0 3px' }}>{`{`}</code> to insert a variable</p>}
                    </div>
                )}

                {action.type === 'sleep' && (
                    <div className="form-group">
                        <label>Duration (seconds):</label>
                        <input type="number" value={action.duration || 0} onChange={e => setAction({ ...action, duration: parseInt(e.target.value) })} />
                    </div>
                )}

                {action.type === 'wait_for_url_change' && (
                    <div className="form-group">
                        <label>Timeout (seconds):</label>
                        <input type="number" value={action.timeout || 30} onChange={e => setAction({ ...action, timeout: parseInt(e.target.value) })} min={1} />
                        <p style={{ fontSize: 11, color: '#888', marginTop: 4 }}>⏳ Waits until the page URL changes, or throws an error after timeout</p>
                    </div>
                )}

                {action.type === 'wait_for_element' && (
                    <>
                        <div className="form-group">
                            <label>Wait Type:</label>
                            <select value={action.wait_type || 'appear'} onChange={e => setAction({ ...action, wait_type: e.target.value as any })}>
                                <option value="appear">Appear (wait until visible)</option>
                                <option value="disappear">Disappear (wait until gone)</option>
                            </select>
                        </div>
                        <div className="form-group">
                            <label>Timeout (seconds):</label>
                            <input type="number" value={action.timeout || 30} onChange={e => setAction({ ...action, timeout: parseInt(e.target.value) })} min={1} />
                        </div>
                        <div className="form-group">
                            <label>Parent Element (optional):</label>
                            <HtmlVariableInput value={action.parent || ''} onChange={val => setAction({ ...action, parent: val })} variables={variables} runtimeVariables={runtimeVariables} />
                        </div>
                        <p style={{ fontSize: 11, color: '#888', marginTop: 4 }}>⏳ Waits for element to {action.wait_type === 'disappear' ? 'disappear from' : 'appear on'} the page</p>
                    </>
                )}

                {action.type === 'switch_to_frame' && (
                    <div className="form-group">
                        <label>Switch Frame By:</label>
                        <select
                            value={action.frame_name !== undefined ? 'name' : 'index'}
                            onChange={e => {
                                if (e.target.value === 'name') setAction({ ...action, frame_name: action.frame_name ?? '', frame_index: undefined });
                                else setAction({ ...action, frame_name: undefined });
                            }}
                            style={{ marginBottom: 8 }}
                        >
                            <option value="index">Index</option>
                            <option value="name">Name / ID</option>
                        </select>
                        <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                            {action.frame_name !== undefined ? (
                                <input
                                    type="text"
                                    value={action.frame_name}
                                    onChange={e => setAction({ ...action, frame_name: e.target.value })}
                                    placeholder="iframe name or id attribute"
                                    style={{ flex: 1 }}
                                />
                            ) : (
                                <input type="number" value={action.frame_index ?? 0} onChange={e => setAction({ ...action, frame_index: parseInt(e.target.value) })} min={-1} style={{ flex: 1 }} />
                            )}
                            <button onClick={handlePlayAction} disabled={!interactiveSessionId || playingAction} style={{ padding: '4px 10px', background: playingAction ? '#888' : !interactiveSessionId ? '#ccc' : '#4CAF50', color: 'white', border: 'none', borderRadius: '4px', cursor: (interactiveSessionId && !playingAction) ? 'pointer' : 'not-allowed', fontSize: '12px', whiteSpace: 'nowrap' }}>{playingAction ? '⏳' : '▶ Play'}</button>
                        </div>
                        {playResult && <p style={{ fontSize: '11px', marginTop: 5, color: playResult.success ? '#4CAF50' : '#f44336' }}>{playResult.message}</p>}
                        {action.frame_name !== undefined
                            ? <p style={{ fontSize: '12px', color: '#666', marginTop: 5 }}>Matches an iframe by its <code>name</code> or <code>id</code> attribute. Use -1 index mode to switch back to main page.</p>
                            : <p style={{ fontSize: '12px', color: '#666', marginTop: 5 }}>Index of the iframe to switch to (0 = first iframe). Use -1 to switch back to main page.</p>
                        }
                    </div>
                )}

                {action.type === 'switch_to_new_window' && (
                    <div className="form-group">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <p style={{ fontSize: '12px', color: '#666', margin: 0 }}>Switches focus to the newest opened tab/window.</p>
                            <button onClick={handlePlayAction} disabled={!interactiveSessionId || playingAction} style={{ padding: '4px 10px', background: playingAction ? '#888' : !interactiveSessionId ? '#ccc' : '#4CAF50', color: 'white', border: 'none', borderRadius: '4px', cursor: (interactiveSessionId && !playingAction) ? 'pointer' : 'not-allowed', fontSize: '12px', whiteSpace: 'nowrap', marginLeft: 8 }}>{playingAction ? '⏳' : '▶ Play'}</button>
                        </div>
                        {playResult && <p style={{ fontSize: '11px', marginTop: 5, color: playResult.success ? '#4CAF50' : '#f44336' }}>{playResult.message}</p>}
                    </div>
                )}

                {(action.type === 'get_element' || action.type === 'get_elements') && (
                    <>
                        <div className="form-group">
                            <label>Store As:</label>
                            <VariableNameInput value={action.store_as || ''} onChange={val => setAction({ ...action, store_as: val })} variables={variables} runtimeVariables={runtimeVariables} />
                            {action.type === 'get_elements' && <p style={{ fontSize: 11, color: '#888', marginTop: 4 }}>💡 Stores an <strong>array of HTML strings</strong> — use a <strong>For Each Loop</strong> node to iterate over them.</p>}
                            {action.type === 'get_element' && <p style={{ fontSize: 11, color: '#888', marginTop: 4 }}>💡 Stores the element's <strong>outer HTML</strong> — use as parent in Click/Write/Hover nodes.</p>}
                        </div>
                        <div className="form-group">
                            <label>Parent Element (optional):</label>
                            <HtmlVariableInput value={action.parent || ''} onChange={val => setAction({ ...action, parent: val })} variables={variables} runtimeVariables={runtimeVariables} />
                        </div>
                    </>
                )}

                {action.type === 'api_call' && (
                    <>
                        <div className="form-group">
                            <label>HTTP Method:</label>
                            <select value={action.method || 'GET'} onChange={e => setAction({ ...action, method: e.target.value })}>
                                <option value="GET">GET</option><option value="POST">POST</option><option value="PUT">PUT</option><option value="DELETE">DELETE</option><option value="PATCH">PATCH</option>
                            </select>
                        </div>
                        <div className="form-group">
                            <label>URL:</label>
                            <div style={{ display: 'flex', gap: '5px' }}>
                                <div style={{ flex: 1 }}>
                                    <VariableTextInput value={action.url || ''} onChange={val => setAction({ ...action, url: val })} placeholder='API endpoint — type { to insert a variable' variables={variables} runtimeVariables={runtimeVariables} />
                                </div>
                                <button onClick={handlePlayAction} disabled={!action.url?.trim() || playingAction} style={{ padding: '4px 10px', background: playingAction ? '#888' : !action.url?.trim() ? '#ccc' : '#4CAF50', color: 'white', border: 'none', borderRadius: '4px', cursor: (action.url?.trim() && !playingAction) ? 'pointer' : 'not-allowed', fontSize: '12px', whiteSpace: 'nowrap' }}>{playingAction ? '⏳' : '▶ Play'}</button>
                            </div>
                            {playResult && <p style={{ fontSize: '11px', marginTop: 5, color: playResult.success ? '#4CAF50' : '#f44336' }}>{playResult.message}</p>}
                            {variables.length > 0 && <p style={{ fontSize: '11px', color: '#888', marginTop: 4 }}>💡 Type <code style={{ background: '#f0f0f0', padding: '0 3px' }}>{`{`}</code> to insert a variable</p>}
                        </div>
                        <div className="form-group">
                            <label>Headers (JSON):</label>
                            <VariableTextArea value={action.headers_raw ?? JSON.stringify(action.headers || {}, null, 2)} onChange={val => {
                                const update: any = { ...action, headers_raw: val };
                                try { update.headers = JSON.parse(val); } catch {}
                                setAction(update);
                            }} rows={4} placeholder='{"Content-Type": "application/json"}' variables={variables} runtimeVariables={runtimeVariables} />
                            <p style={{ fontSize: '11px', color: '#888', marginTop: 4 }}>💡 Type <code style={{ background: '#f0f0f0', padding: '0 3px' }}>{`{`}</code> to insert a variable in header values</p>
                        </div>
                        <div className="form-group">
                            <label>Body (JSON):</label>
                            <VariableTextArea value={action.body_raw ?? JSON.stringify(action.body || {}, null, 2)} onChange={val => {
                                const update: any = { ...action, body_raw: val };
                                try { update.body = JSON.parse(val); } catch {}
                                setAction(update);
                            }} rows={6} placeholder='{"key": "value"}' variables={variables} runtimeVariables={runtimeVariables} />
                            <p style={{ fontSize: '11px', color: '#888', marginTop: 4 }}>💡 Type <code style={{ background: '#f0f0f0', padding: '0 3px' }}>{`{`}</code> to insert a variable in body values</p>
                        </div>
                        <div className="form-group">
                            <label>Store Response As:</label>
                            <VariableNameInput value={action.store_as || ''} onChange={val => setAction({ ...action, store_as: val })} placeholder="e.g. api_response" variables={variables} runtimeVariables={runtimeVariables} />
                            <p style={{ fontSize: 11, color: '#888', marginTop: 4 }}>💡 Stores the response body (parsed as JSON if possible)</p>
                        </div>
                        <div className="form-group">
                            <label>Store HTTP Status As:</label>
                            <VariableNameInput value={action.store_status_as || ''} onChange={val => setAction({ ...action, store_status_as: val })} placeholder="e.g. http_status" variables={variables} runtimeVariables={runtimeVariables} />
                            <p style={{ fontSize: 11, color: '#888', marginTop: 4 }}>💡 Stores the HTTP status code (e.g. 200, 404, 500)</p>
                        </div>
                    </>
                )}

                {action.type === 'last_file' && (
                    <>
                        <div className="form-group">
                            <label>Store As:</label>
                            <VariableNameInput value={action.store_as || ''} onChange={val => setAction({ ...action, store_as: val })} placeholder="e.g. latest_file" variables={variables} runtimeVariables={runtimeVariables} />
                            <p style={{ fontSize: 11, color: '#888', marginTop: 4 }}>💡 Stores the name of the most recently created/modified file in storage</p>
                        </div>
                        <div className="form-group">
                            <button onClick={handlePlayAction} disabled={!action.store_as?.trim() || playingAction} style={{ padding: '6px 16px', background: playingAction ? '#888' : !action.store_as?.trim() ? '#ccc' : '#4CAF50', color: 'white', border: 'none', borderRadius: 4, cursor: (action.store_as?.trim() && !playingAction) ? 'pointer' : 'not-allowed', fontSize: 12, fontWeight: 'bold' }}>
                                {playingAction ? '⏳ Running...' : '▶ Play'}
                            </button>
                            {playResult && <p style={{ fontSize: '11px', marginTop: 5, color: playResult.success ? '#4CAF50' : '#f44336' }}>{playResult.message}</p>}
                        </div>
                    </>
                )}

                {action.type === 'rename_file' && (
                    <>
                        <div className="form-group">
                            <label>Old Name:</label>
                            <VariableTextInput value={action.old_name || ''} onChange={val => setAction({ ...action, old_name: val })} placeholder='Current filename — type { to insert a variable' variables={variables} runtimeVariables={runtimeVariables} />
                        </div>
                        <div className="form-group">
                            <label>New Name:</label>
                            <VariableTextInput value={action.new_name || ''} onChange={val => setAction({ ...action, new_name: val })} placeholder='New filename — type { to insert a variable' variables={variables} runtimeVariables={runtimeVariables} />
                        </div>
                        <div className="form-group">
                            <button onClick={handlePlayAction} disabled={!action.old_name?.trim() || !action.new_name?.trim() || playingAction} style={{ padding: '6px 16px', background: playingAction ? '#888' : (!action.old_name?.trim() || !action.new_name?.trim()) ? '#ccc' : '#4CAF50', color: 'white', border: 'none', borderRadius: 4, cursor: (action.old_name?.trim() && action.new_name?.trim() && !playingAction) ? 'pointer' : 'not-allowed', fontSize: 12, fontWeight: 'bold' }}>
                                {playingAction ? '⏳ Running...' : '▶ Play'}
                            </button>
                            {playResult && <p style={{ fontSize: '11px', marginTop: 5, color: playResult.success ? '#4CAF50' : '#f44336' }}>{playResult.message}</p>}
                            {variables.length > 0 && <p style={{ fontSize: '11px', color: '#888', marginTop: 4 }}>💡 Type <code style={{ background: '#f0f0f0', padding: '0 3px' }}>{`{`}</code> to insert a variable</p>}
                        </div>
                    </>
                )}

                {action.type === 'create_file' && (
                    <>
                        <div className="form-group">
                            <label>File Type:</label>
                            <select value={action.file_type || 'pdf'} onChange={e => setAction({ ...action, file_type: e.target.value as any })}>
                                <option value="pdf">PDF</option>
                                <option value="csv">CSV</option>
                            </select>
                        </div>
                        <div className="form-group">
                            <label>Data (variable):</label>
                            {action.file_type === 'csv'
                                ? <ArrayVariableInput value={action.data || ''} onChange={val => setAction({ ...action, data: val })} variables={variables} runtimeVariables={runtimeVariables} />
                                : <StringVariableInput value={action.data || ''} onChange={val => setAction({ ...action, data: val })} variables={variables} runtimeVariables={runtimeVariables} />
                            }
                            <p style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
                                {action.file_type === 'csv' ? '📊 Variable should contain an array of objects [{}, {}] — keys become column headers' : '📄 Variable should contain a Base64 encoded string'}
                            </p>
                        </div>
                        <div className="form-group">
                            <label>File Name:</label>
                            <VariableTextInput value={action.file_name || ''} onChange={val => setAction({ ...action, file_name: val })} placeholder='e.g. report.pdf — type { to insert a variable' variables={variables} runtimeVariables={runtimeVariables} />
                            {variables.length > 0 && <p style={{ fontSize: '11px', color: '#888', marginTop: 4 }}>💡 Type <code style={{ background: '#f0f0f0', padding: '0 3px' }}>{`{`}</code> to insert a variable</p>}
                        </div>
                        <div className="form-group">
                            <button onClick={handlePlayAction} disabled={!action.data?.trim() || !action.file_name?.trim() || playingAction} style={{ padding: '6px 16px', background: playingAction ? '#888' : (!action.data?.trim() || !action.file_name?.trim()) ? '#ccc' : '#4CAF50', color: 'white', border: 'none', borderRadius: 4, cursor: (action.data?.trim() && action.file_name?.trim() && !playingAction) ? 'pointer' : 'not-allowed', fontSize: 12, fontWeight: 'bold' }}>
                                {playingAction ? '⏳ Running...' : '▶ Play'}
                            </button>
                            {playResult && <p style={{ fontSize: '11px', marginTop: 5, color: playResult.success ? '#4CAF50' : '#f44336' }}>{playResult.message}</p>}
                        </div>
                    </>
                )}

                {action.type === 'read_file' && (
                    <>
                        <div className="form-group">
                            <label>File Type:</label>
                            <select value={action.file_type || 'pdf'} onChange={e => setAction({ ...action, file_type: e.target.value as any })}>
                                <option value="pdf">PDF</option>
                                <option value="csv">CSV</option>
                            </select>
                        </div>
                        <div className="form-group">
                            <label>File Name:</label>
                            <VariableTextInput value={action.file_name || ''} onChange={val => setAction({ ...action, file_name: val })} placeholder='e.g. report.pdf — type { to insert a variable' variables={variables} runtimeVariables={runtimeVariables} />
                            {variables.length > 0 && <p style={{ fontSize: '11px', color: '#888', marginTop: 4 }}>💡 Type <code style={{ background: '#f0f0f0', padding: '0 3px' }}>{`{`}</code> to insert a variable</p>}
                        </div>
                        <div className="form-group">
                            <label>Store As:</label>
                            <VariableNameInput value={action.store_as || ''} onChange={val => setAction({ ...action, store_as: val })} placeholder="Variable name to store result" variables={variables} runtimeVariables={runtimeVariables} />
                            <p style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
                                {action.file_type === 'csv' ? '📊 Result will be an array of objects [{}, {}] — column headers become keys' : '📄 Result will be a string containing the full extracted text of the PDF'}
                            </p>
                        </div>
                        <div className="form-group">
                            <button onClick={handlePlayAction} disabled={!action.file_name?.trim() || !action.store_as?.trim() || playingAction} style={{ padding: '6px 16px', background: playingAction ? '#888' : (!action.file_name?.trim() || !action.store_as?.trim()) ? '#ccc' : '#4CAF50', color: 'white', border: 'none', borderRadius: 4, cursor: (action.file_name?.trim() && action.store_as?.trim() && !playingAction) ? 'pointer' : 'not-allowed', fontSize: 12, fontWeight: 'bold' }}>
                                {playingAction ? '⏳ Running...' : '▶ Play'}
                            </button>
                            {playResult && <p style={{ fontSize: '11px', marginTop: 5, color: playResult.success ? '#4CAF50' : '#f44336' }}>{playResult.message}</p>}
                        </div>
                    </>
                )}

                {action.type === 'ai_agent' && (
                    <>
                        <div className="form-group">
                            <label>System Prompt:</label>
                            <textarea
                                value={action.system_prompt || ''}
                                onChange={e => setAction({ ...action, system_prompt: e.target.value })}
                                placeholder='Define the AI role, e.g. "You are a data analyst..."'
                                rows={3}
                                style={{ width: '100%', boxSizing: 'border-box' }}
                                spellCheck={false}
                            />
                            <p style={{ fontSize: 11, color: '#888', marginTop: 4 }}>🤖 Defines the AI's role and behavior. Static text only.</p>
                        </div>
                        <div className="form-group">
                            <label>User Prompt:</label>
                            <VariableTextArea
                                value={action.user_prompt || ''}
                                onChange={val => setAction({ ...action, user_prompt: val })}
                                placeholder='The task or question for the AI — type { to insert a variable'
                                rows={5}
                                variables={variables}
                                runtimeVariables={runtimeVariables}
                            />
                            <p style={{ fontSize: 11, color: '#888', marginTop: 4 }}>💡 Type <code style={{ background: '#f0f0f0', padding: '0 3px' }}>{`{`}</code> to insert a variable. Specify output format here (e.g. "respond in JSON").</p>
                        </div>
                        <div className="form-group">
                            <label>Store As:</label>
                            <VariableNameInput
                                value={action.store_as || ''}
                                onChange={val => setAction({ ...action, store_as: val })}
                                placeholder="Variable name to store AI response"
                                variables={variables}
                                runtimeVariables={runtimeVariables}
                            />
                        </div>
                        <div className="form-group">
                            <label>Output Type:</label>
                            <select
                                value={action.output_type || 'string'}
                                onChange={e => setAction({ ...action, output_type: e.target.value as 'string' | 'number' | 'json' })}
                                style={{ width: '100%' }}
                            >
                                <option value="string">String — plain text</option>
                                <option value="number">Number — numeric value</option>
                                <option value="json">JSON — object or array</option>
                            </select>
                            <p style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
                                {action.output_type === 'json' ? '🔧 Strips markdown code fences and parses as JSON object/array.' :
                                 action.output_type === 'number' ? '🔢 Stores the response as a number.' :
                                 '📝 Stores the response as plain text.'}
                            </p>
                        </div>
                        <div className="form-group">
                            <button onClick={handlePlayAction} disabled={!action.user_prompt?.trim() || !action.store_as?.trim() || playingAction} style={{ padding: '6px 16px', background: playingAction ? '#888' : (!action.user_prompt?.trim() || !action.store_as?.trim()) ? '#ccc' : '#4CAF50', color: 'white', border: 'none', borderRadius: 4, cursor: (action.user_prompt?.trim() && action.store_as?.trim() && !playingAction) ? 'pointer' : 'not-allowed', fontSize: 12, fontWeight: 'bold' }}>
                                {playingAction ? '⏳ Running...' : '▶ Play'}
                            </button>
                            {playResult && <p style={{ fontSize: '11px', marginTop: 5, color: playResult.success ? '#4CAF50' : '#f44336' }}>{playResult.message}</p>}
                        </div>
                    </>
                )}

                {action.type === 'loop' && (
                    <>
                        <div className="form-group">
                            <label>Iterate Over (array variable):</label>
                            <ArrayVariableInput
                                value={action.iterate_over || ''}
                                onChange={val => setAction({ ...action, iterate_over: val })}
                                variables={variables}
                                runtimeVariables={runtimeVariables}
                            />
                            <p style={{ fontSize: 11, color: '#888', marginTop: 4 }}>💡 Select an array variable from <strong>Get Elements</strong> or a code block.</p>
                        </div>
                        <div className="form-group">
                            <label>Current Item Variable:</label>
                            <VariableNameInput
                                value={action.store_current_as || ''}
                                onChange={val => setAction({ ...action, store_current_as: val })}
                                placeholder="e.g. row (available inside loop as {{row}})"
                                variables={variables}
                                runtimeVariables={runtimeVariables}
                            />
                            <p style={{ fontSize: 11, color: '#888', marginTop: 4 }}>💡 Each iteration sets this variable to the current array item.</p>
                        </div>
                        {action.iterate_over && action.store_current_as && (() => {
                            const iterVar = action.iterate_over;
                            const storeAs = action.store_current_as;
                            const resolveArray = (): any[] | null => {
                                const rt = runtimeVariables[iterVar];
                                if (Array.isArray(rt)) return rt;
                                if (typeof rt === 'string') { try { const p = JSON.parse(rt); if (Array.isArray(p)) return p; } catch {} }
                                const dv = variables.find(v => v.name === iterVar);
                                if (dv && typeof dv.value === 'string') { try { const p = JSON.parse(dv.value); if (Array.isArray(p)) return p; } catch {} }
                                return null;
                            };
                            const arr = resolveArray();
                            if (!arr || arr.length === 0) return null;
                            const currentIdxRaw = runtimeVariables[`__loopIdx_${iterVar}`];
                            let currentIdx = currentIdxRaw !== undefined ? Number(currentIdxRaw) : -1;
                            if (isNaN(currentIdx) || currentIdx < -1 || currentIdx >= arr.length) currentIdx = -1;
                            const nextIdx = currentIdx + 1 < arr.length ? currentIdx + 1 : 0;
                            const isReset = currentIdx + 1 >= arr.length;
                            return (
                                <div className="form-group" style={{ marginTop: 8 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <button
                                            onClick={() => {
                                                const val = arr[nextIdx];
                                                onRuntimeVariablesUpdate?.({ ...runtimeVariables, [storeAs]: typeof val === 'object' ? JSON.stringify(val) : val, [`__loopIdx_${iterVar}`]: nextIdx });
                                                onAppendLog?.('log', `▶ Iteration ${nextIdx + 1}/${arr.length}: ${storeAs} = ${JSON.stringify(val).substring(0, 80)}`, selectedNode?.data?.label || 'Loop');
                                            }}
                                            style={{ padding: '5px 14px', background: '#4CAF50', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, fontWeight: 'bold', whiteSpace: 'nowrap' }}
                                        >▶ {isReset ? 'Restart Iteration' : currentIdx === -1 ? 'Run Iteration' : 'Next Iteration'}</button>
                                        <span style={{ fontSize: 11, color: '#666' }}>
                                            {currentIdx === -1
                                                ? `Will set ${storeAs} = item[0]`
                                                : `${currentIdx + 1} / ${arr.length}`}
                                        </span>
                                    </div>
                                    {currentIdx >= 0 && (
                                        <div style={{ marginTop: 4, fontSize: 11, color: '#007acc', fontFamily: 'monospace', background: '#e8f4ff', padding: '3px 8px', borderRadius: 3, wordBreak: 'break-all' }}>
                                            {storeAs} = {JSON.stringify(arr[currentIdx]).substring(0, 120)}
                                        </div>
                                    )}
                                </div>
                            );
                        })()}
                    </>
                )}

                {(action.type === 'if' || action.type === 'while') && (
                    <>
                        <div className="form-group">
                            <label>Condition Type:</label>
                            <select value={action.condition?.type || 'element_exists'} onChange={e => setAction({ ...action, condition: { ...action.condition!, type: e.target.value as any } })}>
                                <option value="element_exists">Element Exists</option>
                                <option value="element_not_exists">Element Not Exists</option>
                                <option value="text_equals">Text Equals</option>
                                <option value="text_contains">Text Contains</option>
                            </select>
                        </div>
                        <div className="form-group">
                            <label>Condition Locator Method:</label>
                            <select value={action.condition?.locator?.method || 'CSS_SELECTOR'} onChange={e => setAction({ ...action, condition: { ...action.condition!, locator: { ...action.condition?.locator!, method: e.target.value as any } } })}>
                                <option value="CSS_SELECTOR">CSS Selector</option><option value="XPATH">XPath</option><option value="ID">ID</option><option value="NAME">Name</option><option value="CLASS_NAME">Class Name</option>
                            </select>
                        </div>
                        <div className="form-group">
                            <label>Condition Locator Value:</label>
                            <input type="text" value={action.condition?.locator?.value || ''} onChange={e => setAction({ ...action, condition: { ...action.condition!, locator: { ...action.condition?.locator!, value: e.target.value } } })} placeholder="Enter selector" />
                        </div>
                        {(action.condition?.type === 'text_equals' || action.condition?.type === 'text_contains') && (
                            <div className="form-group">
                                <label>Text Value:</label>
                                <input type="text" value={action.condition?.value || ''} onChange={e => setAction({ ...action, condition: { ...action.condition!, value: e.target.value } })} placeholder="Text to compare" />
                            </div>
                        )}
                        <div className="form-group">
                            <p style={{ fontSize: '12px', color: '#666', margin: 0 }}><strong>Note:</strong> Child actions for this {action.type} block should be connected below this node in the flow.</p>
                        </div>
                    </>
                )}

                {action.type === 'code' && (
                    <div className="form-group">
                        <label>JavaScript Code:</label>
                        <div style={{ fontSize: 11, color: '#888', marginBottom: 6, lineHeight: 1.5 }}>
                            💡 Plain assignments (<code>result = ...</code>) update workflow variables. Use <code>let</code>/<code>const</code> for local-only variables. <code style={{ color: '#007acc' }}>console.log()</code> output appears below.
                        </div>
                        <textarea ref={codeTextareaRef} value={action.code || ''} onChange={e => setAction({ ...action, code: e.target.value })}
                            placeholder={`// Examples:\nresult = user_name.toUpperCase();\nconsole.log("Name:", user_name);`}
                            rows={12} spellCheck={false}
                            style={{ fontFamily: '"Courier New", Courier, monospace', fontSize: 13, background: '#1e1e1e', color: '#d4d4d4', border: '1px solid #555', borderRadius: 4, padding: '10px', resize: 'vertical', width: '100%', boxSizing: 'border-box', lineHeight: 1.6 }}
                        />
                        <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                            <button onClick={handlePlayAction} disabled={!action.code?.trim() || playingAction} style={{ padding: '6px 16px', background: playingAction ? '#888' : !action.code?.trim() ? '#ccc' : '#4CAF50', color: 'white', border: 'none', borderRadius: 4, cursor: (action.code?.trim() && !playingAction) ? 'pointer' : 'not-allowed', fontSize: 12, fontWeight: 'bold' }}>
                                {playingAction ? '⏳ Running...' : '▶ Run Code'}
                            </button>
                            {playResult && <span style={{ fontSize: 11, color: playResult.success ? '#4CAF50' : '#f44336' }}>{playResult.success ? '✅' : '❌'}</span>}
                        </div>
                        {playResult && (
                            <pre style={{ marginTop: 8, padding: '8px 10px', background: playResult.success ? '#1e2e1e' : '#2e1e1e', color: playResult.success ? '#69f0ae' : '#ff5252', borderRadius: 4, fontSize: 11, fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-all', maxHeight: 120, overflowY: 'auto', border: `1px solid ${playResult.success ? '#2e4e2e' : '#4e2e2e'}` }}>
                                {playResult.message}
                            </pre>
                        )}
                        {(variables.length > 0 || Object.keys(runtimeVariables).filter(k => !k.startsWith('__')).length > 0) && (
                            <div style={{ marginTop: 8, padding: '8px 10px', background: '#f5f5f5', borderRadius: 4, fontSize: 11 }}>
                                <div style={{ fontWeight: 'bold', marginBottom: 6, color: '#555' }}>📦 Click a variable to insert at cursor:</div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                    {variables.map(v => (
                                        <code key={v.name} onClick={() => {
                                            const textarea = codeTextareaRef.current; if (!textarea) return;
                                            const start = textarea.selectionStart ?? 0; const end = textarea.selectionEnd ?? 0;
                                            const insert = `variables.${v.name}`;
                                            setAction({ ...action, code: (action.code || '').slice(0, start) + insert + (action.code || '').slice(end) });
                                            setTimeout(() => { textarea.focus(); textarea.setSelectionRange(start + insert.length, start + insert.length); }, 0);
                                        }} style={{ background: '#e8f4ff', padding: '3px 8px', borderRadius: 4, color: '#007acc', cursor: 'pointer', border: '1px solid #b3d9ff', userSelect: 'none' }} onMouseEnter={e => (e.currentTarget.style.background = '#c8e6ff')} onMouseLeave={e => (e.currentTarget.style.background = '#e8f4ff')}>
                                            {`variables.${v.name}`}
                                        </code>
                                    ))}
                                    {Object.entries(runtimeVariables).filter(([k]) => !variables.some(v => v.name === k) && !k.startsWith('__')).map(([k]) => (
                                        <code key={`runtime_${k}`} onClick={() => {
                                            const textarea = codeTextareaRef.current; if (!textarea) return;
                                            const start = textarea.selectionStart ?? 0; const end = textarea.selectionEnd ?? 0;
                                            const insert = `variables.${k}`;
                                            setAction({ ...action, code: (action.code || '').slice(0, start) + insert + (action.code || '').slice(end) });
                                            setTimeout(() => { textarea.focus(); textarea.setSelectionRange(start + insert.length, start + insert.length); }, 0);
                                        }} style={{ background: '#fff3e0', padding: '3px 8px', borderRadius: 4, color: '#e65100', cursor: 'pointer', border: '1px solid #ffcc80', userSelect: 'none' }} onMouseEnter={e => (e.currentTarget.style.background = '#ffe0b2')} onMouseLeave={e => (e.currentTarget.style.background = '#fff3e0')}>
                                            {`variables.${k}`} <span style={{ fontSize: 9, fontWeight: 'bold' }}>⚡</span>
                                        </code>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {action.type === 'webhook' && (
                    <div className="form-group">
                        <label>Store Request Body As:</label>
                        <VariableNameInput value={action.store_as || ''} onChange={val => setAction({ ...action, store_as: val })} placeholder="e.g. webhook_data" variables={variables} runtimeVariables={runtimeVariables} />
                        <p style={{ fontSize: 11, color: '#888', marginTop: 6 }}>💡 The incoming POST request body will be stored as a JSON object in this variable.</p>
                        <div style={{ marginTop: 12, padding: '10px 12px', background: '#e8f5e9', borderRadius: 6, border: '1px solid #a5d6a7' }}>
                            <div style={{ fontWeight: 'bold', fontSize: 12, color: '#2e7d32', marginBottom: 6 }}>📡 Webhook Endpoint:</div>
                            {workflowId ? (
                                <>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                                        <code style={{ fontSize: 11, color: '#1b5e20', wordBreak: 'break-all', flex: 1, background: '#f1f8e9', padding: '4px 6px', borderRadius: 3 }}>
                                            POST {getWebhookEndpoint(workflowId)}
                                        </code>
                                        <button onClick={() => navigator.clipboard.writeText(getWebhookEndpoint(workflowId))} title="Copy URL" style={{ padding: '3px 8px', background: '#2e7d32', color: 'white', border: 'none', borderRadius: 3, cursor: 'pointer', fontSize: 11, whiteSpace: 'nowrap' }}>📋 Copy</button>
                                    </div>
                                    <div style={{ fontSize: 11, color: '#555', marginTop: 4 }}>
                                        Example: <code style={{ background: '#f0f0f0', padding: '1px 4px', borderRadius: 3, fontSize: 10 }}>{`curl -X POST ${getWebhookEndpoint(workflowId)} -H "Content-Type: application/json" -d '{"data":"hello"}'`}</code>
                                    </div>
                                </>
                            ) : (
                                <div>
                                    <code style={{ fontSize: 11, color: '#1b5e20' }}>POST {getWebhookEndpointTemplate()}</code>
                                    <div style={{ fontSize: 10, color: '#888', marginTop: 4 }}>💾 Save the workflow first to get the actual URL.</div>
                                </div>
                            )}
                            <div style={{ fontSize: 11, color: '#555', marginTop: 6 }}>Response: <code style={{ background: '#f0f0f0', padding: '1px 4px', borderRadius: 3 }}>HTTP 201</code> — workflow resumes processing.</div>
                        </div>
                        <div style={{ marginTop: 12 }}>
                            <button
                                onClick={handlePlayAction}
                                disabled={!workflowId || playingAction}
                                title={!workflowId ? 'Save the workflow first' : 'Start listening for a test webhook request'}
                                style={{ width: '100%', padding: '8px', background: playingAction ? '#e65100' : !workflowId ? '#ccc' : '#00695C', color: 'white', border: 'none', borderRadius: 4, cursor: (workflowId && !playingAction) ? 'pointer' : 'not-allowed', fontWeight: 'bold', fontSize: 13 }}
                            >
                                {playingAction ? '⏳ Waiting for webhook request...' : '▶ Test: Listen for Request'}
                            </button>
                            {playResult && (
                                <div style={{ marginTop: 8, padding: '8px 10px', background: playResult.success ? '#e8f5e9' : '#ffebee', borderRadius: 4, fontSize: 12, color: playResult.success ? '#2e7d32' : '#c62828', border: `1px solid ${playResult.success ? '#a5d6a7' : '#ef9a9a'}` }}>
                                    {playResult.message}
                                </div>
                            )}
                        </div>
                    </div>
                )}

            </div>
            <div className="properties-footer">
                <button onClick={handleSave} className="btn-save">Save</button>
                <button onClick={onClose} className="btn-cancel">Cancel</button>
            </div>
        </div>
    );
};

export default PropertiesPanel;